import {
  button as buttonEl,
  type AttrValue,
  type ChildNode,
  type ElProps,
  type Mountable,
  type Reactive,
} from '@llui/dom';

/**
 * `<Button>` — the canonical action control (per `30-design-system.md §8`).
 *
 * The single home for the app's button call sites. It maps the product's
 * semantic variant names directly onto the button recipes shipped by
 * `@llui/components/styles/theme.css`:
 *
 *   - `primary`   — the page's main affirmative action (filled accent).
 *   - `secondary` — a neutral / cancel action (outlined surface).
 *   - `ghost`     — a low-emphasis inline action (text-only until hover).
 *   - `danger`    — a destructive action (delete / retire / discard).
 *   - `icon`      — a square icon-only button (toolbar / kebab triggers).
 *
 * Reactive-friendly: `disabled`, `label`, and `className` accept either
 * a static value or a `Signal<…>` so the eventual rewire can carry the
 * app's existing reactive bindings unchanged. Arbitrary attributes
 * (`data-testid`, `aria-*`, `data-*`, reactive bindings) ride through
 * `attrs`, spread onto the root before the managed props.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'icon';

/** LLui ships default and small button sizes. `lg` aliases the default size. */
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonOptions {
  /** Visual emphasis — maps onto the existing app button vocabulary. */
  variant: ButtonVariant;
  /** Control height (per the doc's `--control-height-*`). Defaults to `'md'`. */
  size?: ButtonSize;
  /** Native button type. Defaults to `'button'` (never an implicit submit). */
  type?: 'button' | 'submit' | 'reset';
  /** Text label (string or reactive `text(...)` node). Use `children` for richer content. */
  label?: ChildNode | Reactive<string>;
  /** Rich content — overrides `label` when both are set. */
  children?: readonly ChildNode[];
  /** Click handler. */
  onClick?: (ev: MouseEvent) => void;
  /** Disabled state — static or reactive. */
  disabled?: Reactive<boolean>;
  /** Forwarded `data-testid`. */
  testid?: string;
  /** Accessible label — required for `icon` variants (no visible text). */
  ariaLabel?: AttrValue;
  /** Extra class(es) appended after the variant class — static or reactive. */
  className?: Reactive<string>;
  /**
   * Keep focus where it is when this button is pressed.
   *
   * Set it on any button that lives inside a container which re-renders when
   * an input loses focus. A real mouse press fires `mousedown` first, which
   * blurs the focused input; if that blur re-renders the container, the button
   * is torn down and replaced BEFORE the `click` event lands, so `onClick`
   * never runs. The button looks completely inert — no error, no console
   * output — and only under a real pointer: a synthetic `el.click()` works
   * fine, because it never blurs anything. That asymmetry is the tell.
   *
   * Preventing the `mousedown` default keeps focus on the input, so no
   * re-render happens and the click reaches this button.
   *
   * Known users: the fact row's Save-hint dismiss and History trigger, both of
   * which sit in a row bound to `focusedFactKey`.
   */
  preventBlur?: boolean;
  /** Arbitrary extra attributes merged onto the root (spread first). */
  attrs?: ElProps;
}

/** `class` for the button: base + variant, plus an optional extra class.
 * Reactive when `className` is a signal (the helper threads the signal
 * through `.map`); static otherwise. */
function buttonClass(
  variant: ButtonVariant,
  size: ButtonSize,
  className: Reactive<string> | undefined,
): AttrValue {
  const intent =
    variant === 'primary'
      ? 'btn-primary'
      : variant === 'secondary'
        ? 'btn-secondary'
        : variant === 'danger'
          ? 'btn-danger'
          : variant === 'icon'
            ? 'btn-ghost dl-button-icon'
            : 'btn-ghost';
  const base = `btn ${intent}${size === 'sm' ? ' btn-sm' : ''}`;
  if (className === undefined) return base;
  if (typeof className === 'string') {
    return className === '' ? base : `${base} ${className}`;
  }
  // Signal<string> — derive the combined class reactively.
  return className.map((c) => (c ? `${base} ${c}` : base));
}

export function button(opts: ButtonOptions): Mountable {
  const children: readonly ChildNode[] =
    opts.children !== undefined
      ? opts.children
      : opts.label !== undefined
        ? [opts.label as ChildNode]
        : [];

  const props: ElProps = {
    ...(opts.attrs ?? {}),
    type: opts.type ?? 'button',
    class: buttonClass(opts.variant, opts.size ?? 'md', opts.className),
    ...(opts.disabled !== undefined ? { disabled: opts.disabled } : {}),
    ...(opts.testid !== undefined ? { 'data-testid': opts.testid } : {}),
    ...(opts.ariaLabel !== undefined ? { 'aria-label': opts.ariaLabel } : {}),
    ...(opts.onClick ? { onClick: opts.onClick } : {}),
    ...(opts.preventBlur
      ? { onMouseDown: (ev: Event) => ev.preventDefault() }
      : {}),
  };

  return buttonEl(props, children);
}
